import {
  type RestoreAccount,
  type Account,
  type SourceAccount,
  Provider,
  CloudAccountType,
} from '@repo/api-gw-sdk';

export const isSourceAccount = (
  account?: Account | SourceAccount | RestoreAccount
): account is SourceAccount =>
  !!account && 'sourceAccountAttributes' in account;

export const isRestoreAccount = (
  account?: Account | SourceAccount | RestoreAccount
): account is RestoreAccount =>
  !!account && 'restoreAccountAttributes' in account;

export const getAccountType = (
  account?: Account | SourceAccount | RestoreAccount
): CloudAccountType => {
  if (!account) {
    return CloudAccountType.AccountTypeUnspecified;
  }
  return isSourceAccount(account)
    ? CloudAccountType.Source
    : isRestoreAccount(account)
      ? CloudAccountType.Restore
      : account.accountType;
};

export const getAccountConnectedTime = (
  account: Account | SourceAccount | RestoreAccount
): Date | undefined => {
  if (!account) {
    return undefined;
  }
  return isSourceAccount(account) || isRestoreAccount(account)
    ? account.connectedTime
    : account.createdTime;
};

export const getAccountState = (
  account: Account | SourceAccount | RestoreAccount
) =>
  isSourceAccount(account) || isRestoreAccount(account)
    ? account.status
    : account.state;

export const getAccountRoleArn = (
  account: Account | SourceAccount | RestoreAccount
) => {
  if (isSourceAccount(account)) {
    return account.sourceAccountAttributes.aws?.roleArn;
  }
  if (isRestoreAccount(account)) {
    return account.restoreAccountAttributes.aws?.roleArn;
  }

  return account.role;
};

// getRestoreNodeRoleArn returns the ARN of the EonRestoreNodeRole that lives
// in the given AWS restore account. CFN deploys the node role with a fixed
// name (`EonRestoreNodeRole`) at the same IAM path as the parent restore
// account role (`Path: !Ref RolePath` on both in restore-account.yml), so we
// swap only the trailing role-name segment of the parent ARN and keep its
// partition / region / account / path intact.
//
// Falls back to a root-path ARN when the parent ARN isn't available or is
// shaped unexpectedly — matches the pre-path-support behavior so already-
// onboarded customers (whose parent role is at root) keep getting the same
// output.
export const getRestoreNodeRoleArn = (account: RestoreAccount): string => {
  const NODE_ROLE_NAME = 'EonRestoreNodeRole';
  const parentArn = account.restoreAccountAttributes.aws?.roleArn;
  if (!parentArn) {
    return `arn:aws:iam::${account.providerAccountId}:role/${NODE_ROLE_NAME}`;
  }
  const lastSlash = parentArn.lastIndexOf('/');
  if (lastSlash < 0) {
    return `arn:aws:iam::${account.providerAccountId}:role/${NODE_ROLE_NAME}`;
  }
  return `${parentArn.slice(0, lastSlash + 1)}${NODE_ROLE_NAME}`;
};

export const getAccountServiceAccount = (
  account: Account | SourceAccount | RestoreAccount
) => {
  if (isSourceAccount(account)) {
    return account.sourceAccountAttributes.gcp?.serviceAccount;
  }
  if (isRestoreAccount(account)) {
    return account.restoreAccountAttributes.gcp?.serviceAccount;
  }

  return account.role;
};

export const getAccountProvider = (
  account?: Account | SourceAccount | RestoreAccount
): Provider => {
  if (!account) {
    return Provider.ProviderUnspecified;
  }
  return isSourceAccount(account)
    ? account.sourceAccountAttributes.cloudProvider
    : isRestoreAccount(account)
      ? account.restoreAccountAttributes.cloudProvider
      : account.cloudProvider;
};

export const getCloudAccountNoun = (provider: Provider): string => {
  switch (provider) {
    case Provider.Azure:
      return 'subscription';
    case Provider.Gcp:
    case Provider.MongoAtlas:
      return 'project';
    default:
      return 'account';
  }
};
